<!--

============================================================================================================
||    BlockSelect component                                                                           ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Component renders a list of options to select from.
  Some options can have additional input fields.

|   Features
------------------------------------------------------------------------------------------------------------
  - single select (default)
  - multi select

|   Props
------------------------------------------------------------------------------------------------------------
  - value           : Object    : Used for two-way binding. Contains valid and selected values.
  - options         : Array     : List of options to select from. Each option is an object with title, subtitle, icon, image, type and value properties.
  - multiselect     : Boolean   : If true, enables multi select.
  - preselectFirst  : Boolean   : If true, preselects first option if no value is provided.
  - preventEmpty    : Boolean   : If true, prevents deselecting all options.

|   Events
------------------------------------------------------------------------------------------------------------
  - input       : Object    : Emits object with valid and selected values.

|   v-model
------------------------------------------------------------------------------------------------------------
  - valid             : Boolean   : If true, all selected options are valid.
  - selected          : Array     : List of selected options.
    - additionalData  : Object    : Additional data for each selected option.

|   TODO
------------------------------------------------------------------------------------------------------------
  - preselect first option if no value is provided
  - no-empty - prevents deselecting all options

-->

<template>
  <div class="block-select">
    <div
      v-for="(option, index) in options"
      :key="index"
      class="option flex column"
      :class="{ selected: selected.includes(index) }"
      @click="select(index)" >
      <div class="flex center-all">
        <SBIcon
          v-if="option.icon"
          class="prefix-icon no-shrink"
          :icon="option.icon"
          :color="option.iconColor"
          :size="18"/>
        <img
          v-else-if="option.image"
          :src="option.image"
          height="18px"
          width="18px"
          class="prefix-image no-shrink"
          alt="option image">
        <div class="title greedy">
          <h4
            v-html-safe="option.title"
            class="ellipsis"
            :class="{ double: !option.subtitle }" />
          <p class="ellipsis">{{ option.subtitle }}</p>
        </div>
        <SBIcon
          v-if="selected.includes(index)"
          class="ml-2"
          :icon="SBIconList.DONE_CIRCLE"
          :color="SBColors.bluePrimary"
          :size="14"/>
      </div>
      <!-- EXTENDED section -->
      <div
        v-if="[BlockSelectTypes.KEYWORD, BlockSelectTypes.KEYWORD_SINGLE, BlockSelectTypes.KEYWORD_CONDITION, BlockSelectTypes.TIME].includes(option.type) && selected.includes(index)"
        class="extend SB-border top"
        :class="{ indent: option.icon || option.image }"
        @click.stop >
        <TagInput
          v-if="[BlockSelectTypes.KEYWORD, BlockSelectTypes.KEYWORD_SINGLE, BlockSelectTypes.KEYWORD_CONDITION].includes(option.type) && selected.includes(index)"
          :ref="`extend${index}Ref`"
          v-model="additionalData[index]"
          :show-condition="option.type === BlockSelectTypes.KEYWORD_CONDITION"
          :single-tag="option.type === BlockSelectTypes.KEYWORD_SINGLE"
          basic />
        <TimeFrequency
          v-else-if="[BlockSelectTypes.TIME].includes(option.type) && selected.includes(index)"
          :ref="`extend${index}Ref`"
          v-model="additionalData[index]"
          :min-seconds="option.minSeconds"
          :min-seconds-message="option.minSecondsMessage" />
      </div>
    </div>
  </div>
</template>

<script>
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import BlockSelectTypes from '@/helpers/lists/BlockSelectTypes';
import TagInput from '@/components/common/TagInput';
import TimeFrequency from '@/components/common/TimeFrequency';

export default {
  name: 'BlockSelect',
  components: {
    SBIcon,
    TagInput,
    TimeFrequency,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    options: {
      type: Array,
      required: true,
    },
    multiselect: {
      type: Boolean,
      default: false,
    },
    preselectFirst: {
      type: Boolean,
      default: false,
    },
    preventEmpty: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selected: [],
      additionalData: {},
      SBColors,
      SBIconList,
      BlockSelectTypes,
    };
  },
  watch: {
    additionalData: {
      handler() {
        this.emitChanges();
      },
      deep: true,
    },
    options: {
      handler(newOptions) {
        if (this.preselectFirst) {
          this.$nextTick(() => {
            this.select(0);
          });
        }
        // Set additional data initial values for new options
        newOptions.forEach((option, index) => {
          if (option.type === BlockSelectTypes.TIME) {
            this.$set(this.additionalData, index, { seconds: option.value });
          }
          if ([BlockSelectTypes.KEYWORD, BlockSelectTypes.KEYWORD_SINGLE, BlockSelectTypes.KEYWORD_CONDITION].includes(option.type)) {
            this.$set(this.additionalData, index, { tags: option.tags });
          }
        });
      },
      immediate: true,
    },
  },
  mounted() {
    // Preselect first
    if ((!this.value || !this.value.selected || !this.value.selected.length) && this.preselectFirst && !this.selected.length) {
      this.select(0);
    }
    // Set additional data initial values
    this.options.forEach((option, index) => {
      if (option.type === BlockSelectTypes.TIME) {
        this.$set(this.additionalData, index, { seconds: option.value });
      }
      if ([BlockSelectTypes.KEYWORD, BlockSelectTypes.KEYWORD_SINGLE, BlockSelectTypes.KEYWORD_CONDITION].includes(option.type)) {
        this.$set(this.additionalData, index, { tags: option.tags });
      }
    });
    this.emitChanges();
  },
  methods: {
    focusExtend(index) {
      this.$nextTick(() => {
        if (this.$refs[`extend${index}Ref`] && [BlockSelectTypes.KEYWORD, BlockSelectTypes.KEYWORD_SINGLE, BlockSelectTypes.KEYWORD_CONDITION, BlockSelectTypes.TIME].includes(this.options[index].type)) {
          try {
            this.$refs[`extend${index}Ref`][0].focus();
          } catch (e) { /* Do nothing */ }
        }
      });
    },
    select(index) {
      if (this.multiselect) {
        if (this.selected.includes(index)) {
          this.selected = !this.preventEmpty || this.selected.length > 1 ? this.selected.filter(i => i !== index) : this.selected;
        } else {
          this.selected.push(index);
          this.focusExtend(index);
        }
      } else if (this.selected[0] === index) {
        this.selected = !this.preventEmpty || this.selected.length > 1 ? [] : this.selected;
      } else {
        this.selected = [index];
        this.focusExtend(index);
      }
      this.emitChanges();
    },
    emitChanges() {
      const selectedOptions = this.selected.map(i => ({ id: this.options[i].id, additionalData: this.additionalData[i] }));

      const valid = !!selectedOptions.length && selectedOptions.every((option) => {
        const targetOption = this.options.find(o => option.id === o.id);
        let test;
        if (targetOption) {
          switch (targetOption.type) {
            case BlockSelectTypes.KEYWORD:
            case BlockSelectTypes.KEYWORD_SINGLE:
            case BlockSelectTypes.KEYWORD_CONDITION:
            case BlockSelectTypes.TIME:
              test = option.additionalData ? !!option.additionalData.valid : false;
              break;
            default:
              test = true;
              break;
          }
        } else {
          test = false;
        }

        return test;
      });

      this.$emit('input', {
        valid,
        selected: selectedOptions,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.block-select {
  .option {
    margin: 12px 0;
    padding: 10px 15px 11px;
    border-radius: 10px;
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    border: solid 1px $greyBorder;
    background-color: $white;

    &:first-child {
      margin-top: 0;
    }
    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      cursor: pointer;
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
      border: solid 1px $greyBorder;
      background-color: $greyUltralight;
    }

    &.selected {
      background-color: #fafdff;
    }

    .prefix-icon, .prefix-image {
      margin-right: 12px;
    }

    .title {
      max-width: 100%;
      min-width: 0;

      h4 {
        font-size: 14px;
        font-weight: 500;
        text-align: left;
        color: $blackPrimary;

        ::v-deep * {
          font-weight: 500 !important;
        }

        &.double {
          -webkit-line-clamp: 2;
          max-height: 50px;
          display: -webkit-box;
          margin: auto;
          white-space: break-spaces !important;
          -webkit-box-orient: vertical;
        }
      }

      p {
        font-size: 11px;
        font-weight: normal;
        text-align: left;
        color: $blackUltralight;
        margin-top: 1px;
      }
    }

    .extend {
      margin-top: 12px;
      padding-top: 12px;

      &.indent {
        margin-left: 30px;
      }
    }
  }
}
</style>
